import { useQuery } from '@tanstack/react-query'
import { Printer } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { ZERO_CASH, parseCashAmount } from '@aeris/cash'
import type {
  CashMovementSummary,
  CashSessionSummary,
  DashboardReceivedResponse,
} from '@aeris/contracts'
import {
  MONEY_SCALE,
  add,
  formatMoney,
  fromString,
  toString,
  zero,
} from '@aeris/money'
import { tableReportPrintFor } from '@aeris/printing'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  Pagination,
  QueryState,
  Section,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { fetchReceivedEntries } from '../lib/api/dashboard.ts'
import { fetchCashMovements, fetchCashSessions } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { CASH_KIND_LABELS } from '../lib/cash-labels.ts'
import {
  useCompanyHeader,
  useReceiptTemplate,
} from '../lib/use-receipt-template.ts'

import { PrintDocumentDialog } from './print-document-dialog.tsx'

/**
 * O histórico de fechamentos do caixa — F217.
 *
 * O dono: "tem que ter o histórico de fechamentos de caixa". O fechamento já
 * era gravado (esperado, contado, diferença, quem fechou) e ninguém via: a tela
 * Caixa só mostrava o caixa aberto. Aqui está cada um, do mais novo ao mais
 * velho, e o detalhe de cada fechamento para conferir e imprimir.
 *
 * A gaveta só conhece dinheiro. PIX e cartão não passam por ela, então o
 * detalhe mostra, à parte e dito como tal, o que a LOJA recebeu por forma no
 * horário daquele caixa — para quem pode ver o painel.
 */

const PAGE_SIZE = 10

const money = (value: string): string =>
  formatMoney(parseCashAmount(value) ?? ZERO_CASH)

const dateTime = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})

const when = (iso: string | null): string =>
  iso === null ? '—' : dateTime.format(new Date(iso))

/** Falta, sobra ou confere — pelo sinal da diferença. */
export const DifferenceBadge = ({
  difference,
}: {
  difference: string | null
}): ReactNode => {
  if (difference === null) return null
  const text = difference.trim()
  if (!/[1-9]/.test(text)) return <Badge tone="success">Confere</Badge>
  return text.startsWith('-') ? (
    <Badge tone="danger">{`Falta ${money(text.replace('-', ''))}`}</Badge>
  ) : (
    <Badge tone="warning">{`Sobra ${money(text)}`}</Badge>
  )
}

export const CashHistory = (): ReactNode => {
  const [page, setPage] = useState(1)
  const [open, setOpen] = useState<CashSessionSummary | null>(null)

  const sessions = useQuery({
    // Debaixo de `cash-sessions`: fechar um caixa já invalida esta chave.
    queryKey: ['cash-sessions', 'closed', page],
    queryFn: ({ signal }) =>
      fetchCashSessions(
        {
          status: 'closed',
          page,
          pageSize: PAGE_SIZE,
          sort: 'openedAt',
          direction: 'desc',
        },
        signal
      ),
    placeholderData: (previous) => previous,
  })
  const items = sessions.data?.items ?? []

  return (
    <Section
      title="Fechamentos"
      description="Cada caixa fechado: o que devia ter na gaveta, o que foi contado e a diferença."
    >
      <QueryState
        isPending={sessions.isPending}
        isError={sessions.isError}
        error={sessions.error}
        onRetry={() => {
          void sessions.refetch()
        }}
        what="os fechamentos"
        isEmpty={items.length === 0}
        empty={
          <p className="text-(length:--text-sm) text-(--text-muted)">
            Nenhum caixa fechado ainda.
          </p>
        }
      >
        <TableContainer>
          <Table aria-label="Fechamentos">
            <TableHead>
              <TableRow>
                <TableHeaderCell>Fechado em</TableHeaderCell>
                <TableHeaderCell>Caixa</TableHeaderCell>
                <TableHeaderCell>Quem</TableHeaderCell>
                <TableHeaderCell className="text-right">
                  Esperado
                </TableHeaderCell>
                <TableHeaderCell className="text-right">
                  Contado
                </TableHeaderCell>
                <TableHeaderCell>Diferença</TableHeaderCell>
                <TableHeaderCell>
                  <span className="sr-only">Ver</span>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((session) => (
                <TableRow key={session.id}>
                  <TableCell className="whitespace-nowrap">
                    {when(session.closedAt)}
                  </TableCell>
                  <TableCell>{session.terminalId}</TableCell>
                  <TableCell className="text-(--text-secondary)">
                    {`abriu ${session.openedByName ?? '—'} · fechou ${session.closedByName ?? '—'}`}
                  </TableCell>
                  <TableCell className="text-right font-(--font-mono) tabular-nums">
                    {money(session.expected)}
                  </TableCell>
                  <TableCell className="text-right font-(--font-mono) tabular-nums">
                    {session.countedAmount === null
                      ? '—'
                      : money(session.countedAmount)}
                  </TableCell>
                  <TableCell>
                    <DifferenceBadge difference={session.differenceAmount} />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setOpen(session)
                      }}
                    >
                      Ver
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={sessions.data?.total ?? 0}
          onPageChange={setPage}
          label="Páginas de fechamentos"
        />
      </QueryState>

      {open === null ? null : (
        <CashClosingDialog
          session={open}
          onClose={() => {
            setOpen(null)
          }}
        />
      )}
    </Section>
  )
}

/** O que entrou e saiu da gaveta, por tipo de movimento, sem a abertura. */
const totalsByKind = (
  movements: readonly CashMovementSummary[]
): { label: string; value: string }[] => {
  const sums = new Map<string, ReturnType<typeof zero>>()
  for (const movement of movements) {
    if (movement.kind === 'opening') continue
    const signed = fromString(
      movement.direction === 'out' ? `-${movement.amount}` : movement.amount,
      MONEY_SCALE
    )
    sums.set(
      movement.kind,
      add(sums.get(movement.kind) ?? zero(MONEY_SCALE), signed)
    )
  }
  return [...sums.entries()].map(([kind, sum]) => ({
    label: CASH_KIND_LABELS[kind as CashMovementSummary['kind']],
    value: money(toString(sum)),
  }))
}

/** O que a loja recebeu por forma, somado, no horário do caixa. */
const byMethod = (
  entries: DashboardReceivedResponse['entries']
): { label: string; value: string }[] => {
  const sums = new Map<string, ReturnType<typeof zero>>()
  for (const entry of entries) {
    sums.set(
      entry.methodName,
      add(
        sums.get(entry.methodName) ?? zero(MONEY_SCALE),
        fromString(entry.amount, MONEY_SCALE)
      )
    )
  }
  return [...sums.entries()].map(([name, sum]) => ({
    label: name,
    value: money(toString(sum)),
  }))
}

export const CashClosingDialog = ({
  session,
  onClose,
}: {
  session: CashSessionSummary
  onClose: () => void
}): ReactNode => {
  const { allows } = useAuth()
  const company = useCompanyHeader()
  const template = useReceiptTemplate()
  const [printing, setPrinting] = useState(false)

  const movements = useQuery({
    queryKey: ['cash-sessions', 'closed', session.id, 'movements'],
    // Todas as páginas: um dia cheio passa de cem movimentos, e o fechamento
    // impresso que para no centésimo não bate com a gaveta.
    queryFn: async ({ signal }) => {
      const all: CashMovementSummary[] = []
      for (let page = 1; ; page += 1) {
        const answer = await fetchCashMovements(session.id, signal, {
          sort: 'occurredAt',
          direction: 'asc',
          page,
        })
        all.push(...answer.items)
        if (answer.items.length === 0 || all.length >= answer.total) break
      }
      return all
    },
  })
  const received = useQuery({
    queryKey: [
      'dashboard',
      'received',
      session.openedAt,
      session.closedAt,
      session.branchId,
    ],
    queryFn: ({ signal }) =>
      fetchReceivedEntries(
        {
          from: session.openedAt,
          to: session.closedAt ?? new Date().toISOString(),
          branchId: session.branchId,
        },
        signal
      ),
    enabled: allows('dashboard.read') && session.closedAt !== null,
  })

  const items = movements.data ?? []
  const kinds = totalsByKind(items)
  const methods = byMethod(received.data?.entries ?? [])

  const summary: { label: string; value: string; bold?: boolean }[] = [
    { label: 'Abertura', value: money(session.openingFloat) },
    ...kinds,
    { label: 'Esperado na gaveta', value: money(session.expected), bold: true },
    {
      label: 'Contado',
      value:
        session.countedAmount === null ? '—' : money(session.countedAmount),
      bold: true,
    },
    {
      label: 'Diferença',
      value:
        session.differenceAmount === null
          ? '—'
          : money(session.differenceAmount),
      bold: true,
    },
  ]

  const document = tableReportPrintFor({
    company,
    template,
    title: 'Fechamento de caixa',
    filters: [
      { label: 'Caixa', value: session.terminalId },
      { label: 'Loja', value: session.branchName },
      {
        label: 'Aberto',
        value: `${when(session.openedAt)} · ${session.openedByName ?? '—'}`,
      },
      {
        label: 'Fechado',
        value: `${when(session.closedAt)} · ${session.closedByName ?? '—'}`,
      },
    ],
    summary,
    sections: [
      {
        title: 'Movimentos da gaveta',
        columns: [
          { label: 'Hora' },
          { label: 'Movimento' },
          { label: 'Quem' },
          { label: 'Valor', align: 'right' as const },
        ],
        rows: items.map((movement) => [
          when(movement.occurredAt),
          movement.note === null
            ? CASH_KIND_LABELS[movement.kind]
            : `${CASH_KIND_LABELS[movement.kind]} · ${movement.note}`,
          movement.actorName ?? '—',
          `${movement.direction === 'out' ? '−' : ''}${money(movement.amount)}`,
        ]),
      },
      ...(methods.length === 0
        ? []
        : [
            {
              title: 'Recebido na loja no horário do caixa (todos os caixas)',
              columns: [
                { label: 'Forma' },
                { label: 'Valor', align: 'right' as const },
              ],
              rows: methods.map((method) => [method.label, method.value]),
            },
          ]),
    ],
    ...(session.notes === null
      ? {}
      : { notes: [`Observação: ${session.notes}`] }),
  })

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title={`Fechamento · ${session.terminalId}`}
        description={`${when(session.closedAt)} · fechou ${session.closedByName ?? '—'}`}
        className="max-w-2xl"
      >
        <div className="flex justify-end">
          <Button
            variant="secondary"
            size="sm"
            disabled={movements.isPending}
            leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              setPrinting(true)
            }}
          >
            Imprimir
          </Button>
        </div>

        <QueryState
          isPending={movements.isPending}
          isError={movements.isError}
          error={movements.error}
          onRetry={() => {
            void movements.refetch()
          }}
          what="o fechamento"
          isEmpty={false}
          empty={null}
        >
          <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto">
            <dl
              aria-label="Resumo do fechamento"
              className="flex flex-col gap-1 text-(length:--text-sm)"
            >
              {summary.map((line) => (
                <div key={line.label} className="flex justify-between gap-2">
                  <dt
                    className={
                      line.bold === true
                        ? 'font-(--weight-semibold)'
                        : 'text-(--text-secondary)'
                    }
                  >
                    {line.label}
                  </dt>
                  <dd className="font-(--font-mono) tabular-nums">
                    {line.value}
                  </dd>
                </div>
              ))}
              <div className="flex justify-end">
                <DifferenceBadge difference={session.differenceAmount} />
              </div>
            </dl>

            {methods.length === 0 ? null : (
              <section
                aria-label="Recebido na loja no horário do caixa"
                className="flex flex-col gap-1"
              >
                <h3 className="text-(length:--text-xs) font-(--weight-semibold) text-(--text-secondary)">
                  Recebido na loja no horário deste caixa, por forma
                </h3>
                <p className="text-(length:--text-xs) text-(--text-muted)">
                  Todos os caixas da loja juntos: a gaveta só conhece o
                  dinheiro.
                </p>
                <dl className="flex flex-col gap-1 text-(length:--text-sm)">
                  {methods.map((method) => (
                    <div
                      key={method.label}
                      className="flex justify-between gap-2"
                    >
                      <dt>{method.label}</dt>
                      <dd className="font-(--font-mono) tabular-nums">
                        {method.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}

            <TableContainer>
              <Table aria-label="Movimentos da gaveta">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Hora</TableHeaderCell>
                    <TableHeaderCell>Movimento</TableHeaderCell>
                    <TableHeaderCell>Quem</TableHeaderCell>
                    <TableHeaderCell className="text-right">
                      Valor
                    </TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((movement) => (
                    <TableRow key={movement.id}>
                      <TableCell className="whitespace-nowrap text-(--text-secondary)">
                        {when(movement.occurredAt)}
                      </TableCell>
                      <TableCell>
                        {CASH_KIND_LABELS[movement.kind]}
                        {movement.note === null ? null : (
                          <span className="block text-(length:--text-xs) text-(--text-muted)">
                            {movement.note}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{movement.actorName ?? '—'}</TableCell>
                      <TableCell
                        className={
                          movement.direction === 'out'
                            ? 'text-right font-(--font-mono) tabular-nums text-(--danger)'
                            : 'text-right font-(--font-mono) tabular-nums'
                        }
                      >
                        {`${movement.direction === 'out' ? '−' : ''}${money(movement.amount)}`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </QueryState>
      </DialogContent>

      {printing ? (
        <PrintDocumentDialog
          document={document}
          initialPaper="a4"
          onClose={() => {
            setPrinting(false)
          }}
        />
      ) : null}
    </Dialog>
  )
}
